import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../../shared/utils/request";
import PageLoader from "../shared/pageLoader";
import LoginWarning from "../shared/warningPages";
import AccountSidebar from "../shared/accountSidebar";
import { usePageSeo } from "../../shared/seo";

const MyOrders = () => {
  usePageSeo("myOrders");
  const navigate = useNavigate();

  const [orders, setOrders] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const isToken = localStorage.getItem("token");
  const size = 6;

  const fetchOrders = async (pageNumber = 1, isAppend = false) => {
    try {
      if (pageNumber === 1) setLoading(true);
      else setLoadingMore(true);

      const data = await api.post("customer/getOrders", {
        size,
        page: pageNumber,
      });
      const items = data?.orders || [];
      const total = data?.totalCount ?? 0;
      setTotalCount(total);
      setOrders((prev) => (isAppend ? [...prev, ...items] : items));
      setHasMore(total > pageNumber * size);
    } catch (error) {
      console.error("Failed to fetch orders:", error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    if (isToken) {
      setPage(1);
      fetchOrders(1, false);
    } else {
      setLoading(false);
    }
  }, [isToken]);

  const loadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchOrders(nextPage, true);
  };

  if (!isToken && !loading) return <LoginWarning />;

  if (!loading && orders.length === 0) {
    return (
      <section className="py-40">
        <div className="container container-lg">
          <div className="row g-3">
            <div className="col-lg-3 mb-16 mb-lg-0">
              <AccountSidebar />
            </div>
            <div className="col-lg-9">
              <div className="text-center py-80 bg-white border border-gray-100 rounded-8">
                <i className="ph ph-package text-6xl text-neutral-400 mb-16" />
                <h3 className="mb-12">No Orders Yet</h3>
                <p className="text-neutral-600 mb-32">
                  Start shopping and your orders will appear here
                </p>
                <Link to="/" className="btn btn-main">
                  <i className="ph ph-storefront me-8" />
                  Start Shopping
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  const getStatusColor = (status) => {
    const colors = {
      pending: "warning",
      processing: "info",
      completed: "success",
      cancelled: "danger",
      delivered: "success",
    };
    return colors[status?.toLowerCase()] || "secondary";
  };

  return (
    <>
      {loading && <PageLoader />}
      <section
        className="py-40"
      >
        <div className="container container-lg">
          <div className="row g-3">
            <div className="col-lg-3 mb-16 mb-lg-0">
              <AccountSidebar />
            </div>
            <div className="col-lg-9">
              <div className="mb-24 d-flex align-items-center justify-content-between flex-wrap gap-2">
                <div>
                  <h3 className="mb-2 fw-bold text-main-700">My Orders</h3>
                  <p className="text-neutral-600 text-sm mb-0 d-none d-sm-block">
                    Track and manage your orders
                  </p>
                </div>
                <div className="bg-white rounded-8 px-12 py-6 shadow-sm border border-main-200">
                  <span className="text-xs text-neutral-600">Total: </span>
                  <span className="fw-bold text-main-600">{totalCount}</span>
                </div>
              </div>

              <div className="row g-3">
                {orders?.map((order) => (
                  <div
                    key={order?.order_code}
                    className="col-12 col-md-6"
                    style={{ cursor: "pointer" }}
                    onClick={() => {
                      navigate(`/my-orders/${order?.order_code}`);
                    }}
                  >
                    <div
                      className="rounded-12 p-12 p-sm-16 transition bg-white h-100 border border-main-100"
                      style={{
                        boxShadow: "0 2px 8px rgba(26, 107, 48, 0.06)",
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.boxShadow =
                          "0 8px 24px rgba(26, 107, 48, 0.12)")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.boxShadow =
                          "0 2px 8px rgba(26, 107, 48, 0.06)")
                      }
                    >
                      <div className="d-flex flex-column gap-12">
                        <div className="d-flex align-items-center gap-8">
                          <div
                            className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0 bg-main-600"
                          >
                            <i className="ph ph-package text-white" />
                          </div>
                          <div className="flex-grow-1 overflow-hidden">
                            <h6
                              className="mb-0 fw-bold text-truncate text-main-700"
                              style={{ fontSize: "14px" }}
                            >
                              #{order?.order_code}
                            </h6>
                          </div>
                        </div>

                        <div className="d-flex align-items-center gap-6 flex-wrap">
                          <span
                            className={`badge bg-${getStatusColor(order.status)} text-white px-8 py-4 rounded-pill`}
                            style={{
                              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                              fontSize: "10px",
                            }}
                          >
                            {order.status}
                          </span>
                          <span className="text-xs text-neutral-500 d-flex align-items-center gap-4">
                            <i className="ph ph-calendar-blank" />
                            <span className="d-none d-sm-inline">
                              {new Date(order.order_date).toLocaleDateString(
                                "en-US",
                                {
                                  month: "short",
                                  day: "numeric",
                                  year: "numeric",
                                },
                              )}
                            </span>
                            <span className="d-inline d-sm-none">
                              {new Date(order.order_date).toLocaleDateString(
                                "en-US",
                                { month: "short", day: "numeric" },
                              )}
                            </span>
                          </span>
                        </div>

                        <div
                          className="d-flex flex-column gap-8 py-8"
                          style={{
                            borderTop: "1px solid #e8f5e9",
                            borderBottom: "1px solid #e8f5e9",
                          }}
                        >
                          {order.delivery_date && (
                            <div className="d-flex align-items-center gap-6">
                              <i
                                className="ph ph-truck text-main-600"
                                style={{ fontSize: "14px" }}
                              />
                              <span className="text-xs text-neutral-700">
                                <span className="fw-semibold">Delivery:</span>{" "}
                                {new Date(
                                  order.delivery_date,
                                ).toLocaleDateString("en-US", {
                                  month: "short",
                                  day: "numeric",
                                  year: "numeric",
                                })}
                                {order.delivery_slot && (
                                  <span className="text-neutral-500">
                                    {" "}
                                    • {order.delivery_slot}
                                  </span>
                                )}
                              </span>
                            </div>
                          )}

                          {order.delivery_boy_id ? (
                            <div className="d-flex align-items-center gap-6">
                              <i
                                className="ph ph-user-circle text-success"
                                style={{ fontSize: "14px" }}
                              />
                              <span className="text-xs text-neutral-700">
                                <span className="fw-semibold">{"Akhil"}</span>
                                {order.delivery_boy_phone && (
                                  <a
                                    href={`tel:${order.delivery_boy_phone}`}
                                    className="text-main-600 ms-4 text-decoration-none"
                                  >
                                    <i className="ph ph-phone" />{" "}
                                    {order.delivery_boy_phone}
                                  </a>
                                )}
                              </span>
                            </div>
                          ) : (
                            <div className="d-flex align-items-center gap-6">
                              <i
                                className="ph ph-user-circle text-neutral-400"
                                style={{ fontSize: "14px" }}
                              />
                              <span className="text-xs text-neutral-500">
                                Delivery boy not assigned
                              </span>
                            </div>
                          )}

                          <div className="d-flex align-items-center gap-6">
                            <i
                              className={`ph ${order.payment_type?.toLowerCase() === "cod" ? "ph-money" : "ph-credit-card"} text-main-500`}
                              style={{ fontSize: "14px" }}
                            />
                            <span className="text-xs text-neutral-700">
                              <span className="fw-semibold">
                                {order.payment_type}
                              </span>
                              <span
                                className={`ms-6 badge ${order.payment_status?.toLowerCase() === "completed" ? "bg-success" : "bg-warning"} text-white px-6 py-2`}
                                style={{ fontSize: "9px" }}
                              >
                                {order.payment_status}
                              </span>
                            </span>
                          </div>
                        </div>

                        <div
                          className="px-12 py-8 rounded-8"
                          style={{
                            background: "linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)",
                          }}
                        >
                          <div className="d-flex justify-content-between align-items-center">
                            <span className="text-xs text-neutral-600">
                              Total Amount
                            </span>
                            <h5
                              className="mb-0 fw-bold text-main-700"
                              style={{ fontSize: "18px" }}
                            >
                              ₹{order.total_amount}
                            </h5>
                          </div>
                          <span className="text-xs text-neutral-500">
                            {order?.items?.length} item(s)
                          </span>
                        </div>

                        <div
                          className="d-flex gap-6 justify-content-end pt-8"
                          style={{ borderTop: "1px solid #e8f5e9" }}
                        >
                          <Link
                            to={`/my-orders/${order?.order_code}`}
                            className="d-flex align-items-center justify-content-center rounded-circle transition bg-main-600 text-white border-0"
                            style={{
                              width: "40px",
                              height: "40px",
                              boxShadow: "0 4px 12px rgba(26, 107, 48, 0.35)",
                            }}
                            title="View Details"
                            onMouseEnter={(e) =>
                              (e.currentTarget.style.transform = "scale(1.1)")
                            }
                            onMouseLeave={(e) =>
                              (e.currentTarget.style.transform = "scale(1)")
                            }
                          >
                            <i className="ph ph-arrow-right text-lg" />
                          </Link>
                          {/* <a
                            href={""}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="d-flex align-items-center justify-content-center rounded-circle transition bg-white border border-main-200 text-main-600"
                            style={{
                              width: "40px",
                              height: "40px",
                              boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
                            }}
                            title="Download Invoice"
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = "scale(1.1)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = "scale(1)";
                            }}
                          >
                            <i className="ph ph-download-simple text-lg" />
                          </a> */}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {hasMore && orders.length > 0 && (
                <div className="text-center mt-40">
                  <button
                    type="button"
                    onClick={loadMore}
                    disabled={loadingMore}
                    className="btn btn-outline-main px-40 py-12 rounded-pill fw-semibold"
                  >
                    {loadingMore ? (
                      <span
                        className="spinner-border spinner-border-sm me-4"
                        aria-hidden="true"
                      />
                    ) : null}
                    Load More
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default MyOrders;
